/**
 * Audit log export routes, declared once with `defineRoute`.
 *
 * The first real route contract (#4105): the shapes of the export job, its filters and its list are INFERRED from the
 * schemas below, so the interfaces `AuditExportFilters`, `AuditExportResponse` and `AuditExportListResponse` are no
 * longer written by hand. The backend registers each route from its contract (`registerContractRoute`) and the
 * frontend calls it with `callContract`.
 */

import { z } from 'zod/v4'

import { defineRoute } from '../../contract/index.js'

import {
  AuditAction,
  AuditExportFormat,
  AuditExportStatus,
  ResourceType,
} from './audit-log.types.js'

const auditExportFiltersSchema = z.object({
  tenantId: z.uuid().optional(),
  userId: z.uuid().optional(),
  action: z.enum(AuditAction).optional(),
  resourceType: z.enum(ResourceType).optional(),
  dateFrom: z.iso.datetime().optional(),
  dateTo: z.iso.datetime().optional(),
  search: z.string().max(200).optional(),
})
export type AuditExportFilters = z.infer<typeof auditExportFiltersSchema>

const auditExportSchema = z.object({
  /** The journal event's id as a GUID (`00000000-…-<event id>`): no RFC version nibble, so `guid`, not `uuid`. */
  id: z.guid(),
  format: z.enum(AuditExportFormat),
  status: z.enum(AuditExportStatus),
  progress: z.number(),
  filters: auditExportFiltersSchema,
  fileName: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  recordCount: z.number().nullable(),
  errorMessage: z.string().nullable(),
  emailRecipient: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  estimatedTime: z.string().optional(),
})
export type AuditExportResponse = z.infer<typeof auditExportSchema>

const adminOffsetPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
})

const auditExportListSchema = z.object({
  items: z.array(auditExportSchema),
  pagination: adminOffsetPaginationSchema,
})
export type AuditExportListResponse = z.infer<typeof auditExportListSchema>

/** The body of an export request: strict at every level, so a key nobody declared is a 400 and not silently dropped. */
export const requestAuditExportBodySchema = z.strictObject({
  format: z.enum(AuditExportFormat),
  filters: auditExportFiltersSchema.strict(),
  emailRecipient: z.email().optional(),
})

export const auditLogRoutes = {
  listExports: defineRoute({
    id: 'audit.exports.list',
    method: 'GET',
    path: '/api/admin/system/audit/exports',
    access: 'super_admin',
    query: z.object({
      page: z.coerce.number<number>().int().min(1).default(1),
      limit: z.coerce.number<number>().int().min(1).max(100).default(20),
    }),
    response: auditExportListSchema,
    summary: 'List the audit log export jobs of the current user',
    tags: ['admin', 'audit'],
  }),
  requestExport: defineRoute({
    id: 'audit.exports.request',
    method: 'POST',
    path: '/api/admin/system/audit/export',
    access: 'super_admin',
    body: requestAuditExportBodySchema,
    response: auditExportSchema,
    status: 201,
    summary: 'Request an asynchronous audit log export (CSV, JSON or PDF)',
    tags: ['admin', 'audit'],
  }),
  getExport: defineRoute({
    id: 'audit.exports.get',
    method: 'GET',
    path: '/api/admin/system/audit/exports/:id',
    access: 'super_admin',
    params: z.object({ id: z.guid() }),
    response: auditExportSchema,
    summary: 'Get the status of an audit log export job',
    tags: ['admin', 'audit'],
  }),
  downloadExport: defineRoute({
    id: 'audit.exports.download',
    method: 'GET',
    path: '/api/admin/system/audit/exports/:id/download',
    access: 'super_admin',
    params: z.object({ id: z.guid() }),
    response: { kind: 'file', contentTypes: ['text/csv', 'application/json', 'application/pdf'] },
    summary: 'Download the file of a completed audit log export',
    tags: ['admin', 'audit'],
  }),
} as const
